'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toggleSocialPostReaction } from '@/src/actions/social-posts';
import { patchInfinitePosts } from '@/src/utils/patchInfinitePosts';
import { SocialReaction } from 'types/src/types';

type Vars = {
  postId: number;
  reaction: SocialReaction;
};

type Options = {
  onLocalPostUpdate?: (updater: any) => void;
};

function ensureCounts(p: any) {
  const likes = p?.reactionCounts?.likes ?? 0;
  const dislikes = p?.reactionCounts?.dislikes ?? 0;
  return { likes, dislikes, score: likes - dislikes };
}

function applyOptimisticReaction(p: any, reaction: SocialReaction) {
  const counts = ensureCounts(p);
  const prev = (p.myReaction ?? null) as SocialReaction | null;

  let likes = counts.likes;
  let dislikes = counts.dislikes;
  let next: SocialReaction | null = prev;

  if (reaction === 'like') {
    if (prev === 'like') {
      likes = Math.max(0, likes - 1);
      next = null;
    } else if (prev === 'dislike') {
      dislikes = Math.max(0, dislikes - 1);
      likes = likes + 1;
      next = 'like';
    } else {
      likes = likes + 1;
      next = 'like';
    }
  } else {
    if (prev === 'dislike') {
      dislikes = Math.max(0, dislikes - 1);
      next = null;
    } else if (prev === 'like') {
      likes = Math.max(0, likes - 1);
      dislikes = dislikes + 1;
      next = 'dislike';
    } else {
      dislikes = dislikes + 1;
      next = 'dislike';
    }
  }

  return {
    ...p,
    myReaction: next,
    reactionCounts: { likes, dislikes, score: likes - dislikes },
  };
}

export function useToggleReactionMutation(opts?: Options) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ postId, reaction }: Vars) => {
      const res = await toggleSocialPostReaction(postId, reaction);
      if (res.error) throw new Error(res.error);
      return res.data;
    },

    onMutate: async ({ postId, reaction }) => {
      const queries = qc.getQueryCache().findAll({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          ['profilePosts', 'discoverPosts', 'followedPosts'].includes(
            q.queryKey[0] as string
          ),
      });

      const snapshots = queries.map((q) => ({
        key: q.queryKey,
        data: qc.getQueryData(q.queryKey),
      }));

      await Promise.all(
        queries.map((q) => qc.cancelQueries({ queryKey: q.queryKey }))
      );

      queries.forEach((q) => {
        qc.setQueryData(q.queryKey, (data: any) =>
          patchInfinitePosts(data, postId, (p) =>
            applyOptimisticReaction(p, reaction)
          )
        );
      });

      if (opts?.onLocalPostUpdate) {
        opts.onLocalPostUpdate((prev: any) =>
          prev?.id === postId ? applyOptimisticReaction(prev, reaction) : prev
        );
      }

      return { snapshots };
    },

    onError: (_err, _vars, ctx) => {
      ctx?.snapshots?.forEach(({ key, data }) => qc.setQueryData(key, data));
    },
    onSuccess: (serverData, { postId }) => {
      if (!serverData) return;

      const { myReaction, counts } = serverData as any;

      const queries = qc.getQueryCache().findAll({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          ['profilePosts', 'discoverPosts', 'followedPosts'].includes(
            q.queryKey[0] as string
          ),
      });

      queries.forEach((q) => {
        qc.setQueryData(q.queryKey, (data: any) =>
          patchInfinitePosts(data, postId, (p) => ({
            ...p,
            myReaction: myReaction ?? null,
            reactionCounts: counts ?? p.reactionCounts,
          }))
        );
      });

      if (opts?.onLocalPostUpdate) {
        opts.onLocalPostUpdate((prev: any) =>
          prev?.id === postId
            ? {
                ...prev,
                myReaction: myReaction ?? null,
                reactionCounts: counts ?? prev.reactionCounts,
              }
            : prev
        );
      }
    },

    onSettled: () => {
      qc.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          ['profilePosts', 'discoverPosts', 'followedPosts'].includes(
            q.queryKey[0] as string
          ),
      });
    },
  });
}
